/**
 * Parses PlantUML class-diagram DSL → Canvas elements.
 * Supports: class, interface, abstract class, enum, package, note,
 * relations (--|>, ..|>, *--, o--, -->, ..>, --, ..)
 */

import { createElement } from '../types/elements';
import { textWidth } from './elementUtils';

export interface ErdField {
  visibility: string; // '+' | '-' | '#' | '~' | ''
  name: string;
  type: string;
  abstract: boolean;
}

export interface ErdClassMeta {
  kind: 'class' | 'interface' | 'enum' | 'abstract';
  name: string;
  stereotype?: string;
  fields: ErdField[];
  methods: ErdField[];
}

// ── Layout constants (used by ShapeRenderer too) ──────────────────────────
export const ERD_LINE_H = 20;
export const ERD_HEADER_H_BASE = 28;      // no stereotype
export const ERD_HEADER_H_STEREO = 44;   // with stereotype
export const ERD_SECTION_PAD = 4;

export function computeErdHeight(erd: ErdClassMeta): number {
  const headerH = erd.stereotype ? ERD_HEADER_H_STEREO : ERD_HEADER_H_BASE;
  const fieldsH = (erd.fields.length > 0 ? erd.fields.length * ERD_LINE_H : 0) + ERD_SECTION_PAD * 2;
  const methodsH = erd.methods.length > 0
    ? erd.methods.length * ERD_LINE_H + ERD_SECTION_PAD * 2
    : 0;
  return headerH + fieldsH + methodsH;
}

// ── Field parser ───────────────────────────────────────────────────────────

const VIS_RE = /^([+\-#~])/;
const ABSTRACT_RE = /\{abstract\}/i;

function parseFieldLine(raw: string): ErdField {
  const noAbs = raw.replace(ABSTRACT_RE, '').trim();
  const vis = VIS_RE.exec(noAbs);
  const rest = vis ? noAbs.slice(1).trim() : noAbs;
  const colonIdx = rest.lastIndexOf(':');
  const name = colonIdx >= 0 ? rest.slice(0, colonIdx).trim() : rest;
  const type = colonIdx >= 0 ? rest.slice(colonIdx + 1).trim() : '';
  return {
    visibility: vis ? vis[1] : '',
    name,
    type,
    abstract: ABSTRACT_RE.test(raw),
  };
}

// ── Relation patterns (order matters — more specific first) ────────────────

interface RelPattern {
  re: RegExp;
  startCap: string;
  endCap: string;
  dashed: boolean;
}

// Optional PlantUML multiplicity annotation: "1", "0..*", "1..*", "0..1", etc.
// Appears between the class name and the relation symbol (and/or between the
// symbol and the target class name). Groups: (from) (to) (label?).
const CARD = String.raw`(?:\s+"[^"]*")?`;   // optional cardinality after FROM
const CARD2 = String.raw`(?:"[^"]*"\s+)?`;  // optional cardinality before TO

const REL_PATTERNS: RelPattern[] = [
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+\.\.\|>\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-inheritance', dashed: true },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+--\|>\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-inheritance', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+\*--\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'erd-composition', endCap: 'none', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+--\*\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-composition', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+o--\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'erd-aggregation', endCap: 'none', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+--o\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-aggregation', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+-->\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-association', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+<--\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'erd-association', endCap: 'none', dashed: false },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+\.\.>\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'erd-association', dashed: true },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+<\.\.\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'erd-association', endCap: 'none', dashed: true },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+\.\.\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'none', dashed: true },
  { re: new RegExp(String.raw`^(\S+)${CARD}\s+--\s+${CARD2}(\S+)(?:\s*:\s*(.*))?$`), startCap: 'none', endCap: 'none', dashed: false },
];

// ── Header regexes ─────────────────────────────────────────────────────────

const CLASS_HEADER_RE = /^(abstract\s+class|abstract|class|interface)\s+"?([^"{\s]+)"?(?:\s+<<([^>]+)>>)?(?:\s+as\s+(\S+))?/i;
const ENUM_HEADER_RE = /^enum\s+"?([^"{\s]+)"?(?:\s+<<([^>]+)>>)?/i;
const PACKAGE_RE = /^package\s+"?([^"{\n]+?)"?\s*(?:\{)?$/i;
const NOTE_RE = /^note\s+"([^"]+)"\s+as\s+(\S+)/i;
const NOTE_SINGLE_RE = /^note\s+"([^"]+)"/i;
const STEREOTYPE_DECL_RE = /^"([^"]+)"\s+<<([^>]+)>>/;

// ── Auto-layout ────────────────────────────────────────────────────────────

const CLASS_W = 200;
const BASE_GAP = 60;           // minimum corridor between adjacent BFS layers
const LABEL_PAD = 8;           // extra padding for label text in the gap
const PORT_MARGIN = 10;
const BEND_THRESHOLD_DEG = 20;
const ROUTE_SPREAD = 20;
const SELF_REF_LOOP_W = 28;  // width of self-referential loop extending right of the table
const ORIGIN_X = 24800;
const ORIGIN_Y = 24700;
const MAX_PER_COL = 5;         // max tables per visual column within a BFS layer
const SUBCOL_GAP  = 40;        // horizontal gap between sub-columns in the same layer

// ── Main parser ────────────────────────────────────────────────────────────

interface Relation {
  from: string;
  to: string;
  startCap: string;
  endCap: string;
  dashed: boolean;
  label?: string;
  fromField?: string;  // field in FROM class the arrow originates at
  toField?: string;    // field in TO class the arrow lands at
}

interface ClassLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface RoutedRelation extends Relation {
  routeId: string;
  relIndex: number;
  startSide: 'top' | 'right' | 'bottom' | 'left';
  endSide: 'top' | 'right' | 'bottom' | 'left';
  dominantAxis: 'horizontal' | 'vertical';
  bent: boolean;
  startFieldY?: number;  // canvas Y of the fromField row (overrides port spread)
  endFieldY?: number;    // canvas Y of the toField row
}

const relationLabelGap = (label?: string): number =>
  Math.max(BASE_GAP, label ? textWidth(label, 11) + LABEL_PAD * 2 : BASE_GAP);

const getClassCenter = (box: ClassLayout) => ({
  x: box.x + box.w / 2,
  y: box.y + box.h / 2,
});

const chooseRouteShape = (fromBox: ClassLayout, toBox: ClassLayout) => {
  const fromCenter = getClassCenter(fromBox);
  const toCenter = getClassCenter(toBox);
  const dx = toCenter.x - fromCenter.x;
  const dy = toCenter.y - fromCenter.y;
  const dominantAxis = Math.abs(dx) >= Math.abs(dy) ? 'horizontal' : 'vertical';
  const angleFromAxisDeg =
    dominantAxis === 'horizontal'
      ? Math.atan2(Math.abs(dy), Math.max(1, Math.abs(dx))) * (180 / Math.PI)
      : Math.atan2(Math.abs(dx), Math.max(1, Math.abs(dy))) * (180 / Math.PI);

  return {
    dominantAxis,
    bent: angleFromAxisDeg > BEND_THRESHOLD_DEG,
    startSide:
      dominantAxis === 'horizontal'
        ? dx >= 0
          ? 'right'
          : 'left'
        : dy >= 0
        ? 'bottom'
        : 'top',
    endSide:
      dominantAxis === 'horizontal'
        ? dx >= 0
          ? 'left'
          : 'right'
        : dy >= 0
        ? 'top'
        : 'bottom',
  } as Pick<RoutedRelation, 'dominantAxis' | 'bent' | 'startSide' | 'endSide'>;
};

// Hierarchical BFS layout (Sugiyama-lite).
// Sources go in column 0; each BFS level adds a column to the right.
// Within each column classes are stacked vertically with a fixed gap.
// This guarantees that arrows from a parent always go horizontally to the
// right (or at a shallow angle), so no U-shapes appear regardless of how
// many tables the diagram has.
const buildClassLayouts = (
  classOrder: string[],
  classes: Map<string, ErdClassMeta>,
  relations: Relation[],
  gap: number
): Map<string, ClassLayout> => {
  const n = classOrder.length;
  if (n === 0) return new Map();

  // ── 1. Build directed adjacency ──────────────────────────────────────────
  const outEdges = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  classOrder.forEach((a) => { outEdges.set(a, []); inDegree.set(a, 0); });

  for (const rel of relations) {
    if (!classes.has(rel.from) || !classes.has(rel.to) || rel.from === rel.to) continue;
    outEdges.get(rel.from)!.push(rel.to);
    inDegree.set(rel.to, (inDegree.get(rel.to) || 0) + 1);
  }

  // ── 2. BFS layering from sources ────────────────────────────────────────
  const layer = new Map<string, number>();

  // Nodes with no incoming edges are sources → column 0
  const queue: string[] = [];
  classOrder.forEach((a) => {
    if ((inDegree.get(a) || 0) === 0) { layer.set(a, 0); queue.push(a); }
  });

  // Handle fully-cyclic graphs: seed with highest out-degree node
  if (queue.length === 0) {
    let best = classOrder[0];
    let bestOut = -1;
    classOrder.forEach((a) => {
      const d = outEdges.get(a)!.length;
      if (d > bestOut) { bestOut = d; best = a; }
    });
    layer.set(best, 0);
    queue.push(best);
  }

  while (queue.length > 0) {
    const curr = queue.shift()!;
    const currLayer = layer.get(curr)!;
    for (const next of outEdges.get(curr)!) {
      const proposed = currLayer + 1;
      if (!layer.has(next) || layer.get(next)! < proposed) {
        layer.set(next, proposed);
        queue.push(next);
      }
    }
  }

  // Assign any remaining unvisited nodes (disconnected components)
  // via undirected BFS from already-layered nodes
  const undirAdj = new Map<string, string[]>();
  classOrder.forEach((a) => undirAdj.set(a, []));
  for (const rel of relations) {
    if (!classes.has(rel.from) || !classes.has(rel.to)) continue;
    undirAdj.get(rel.from)!.push(rel.to);
    undirAdj.get(rel.to)!.push(rel.from);
  }
  classOrder.forEach((a) => {
    if (layer.has(a)) return;
    const nb = (undirAdj.get(a) || []).find((b) => layer.has(b));
    layer.set(a, nb !== undefined ? (layer.get(nb)! + 1) : 0);
  });

  // ── 3. Group by layer, preserve classOrder within each layer ────────────
  const byLayer = new Map<number, string[]>();
  classOrder.forEach((a) => {
    const l = layer.get(a) || 0;
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l)!.push(a);
  });

  // Sort within each layer by the average layer of their neighbours so
  // that connections to adjacent columns cross as little as possible.
  byLayer.forEach((aliases) => {
    aliases.sort((a, b) => {
      const avgLayer = (alias: string) => {
        const nbrs = undirAdj.get(alias) || [];
        if (!nbrs.length) return 0;
        return nbrs.reduce((s, nb) => s + (layer.get(nb) || 0), 0) / nbrs.length;
      };
      return avgLayer(a) - avgLayer(b);
    });
  });

  // ── 4. Compute canvas positions (sub-column aware + vertical centering) ──
  // If a BFS layer has more than MAX_PER_COL tables they overflow into adjacent
  // sub-columns so no single visual column grows unreasonably tall.
  // Each sub-column is vertically centered relative to the globally tallest one
  // so that arrows between adjacent layers are as horizontal as possible.
  // Vertical row gap: just enough for elbow routing — labels live on the elbow.
  const ROW_GAP = BASE_GAP;

  const sortedLayerNums = Array.from(byLayer.keys()).sort((a, b) => a - b);
  let currentX = ORIGIN_X;
  const layerStartX = new Map<number, number>();
  const layerSubColCount = new Map<number, number>();

  sortedLayerNums.forEach((l) => {
    const count = (byLayer.get(l) || []).length;
    const numSubCols = Math.max(1, Math.ceil(count / MAX_PER_COL));
    layerStartX.set(l, currentX);
    layerSubColCount.set(l, numSubCols);
    // Inter-layer corridor only needs to fit the elbow margin + the widest
    // straight-arrow label.  Labels for bent arrows now ride the elbow's
    // longest segment (not the gap), so we no longer multiply this.
    const layerGap = Math.max(BASE_GAP, gap);
    currentX += numSubCols * CLASS_W + (numSubCols - 1) * SUBCOL_GAP + layerGap;
  });

  // First pass: total visual height per sub-column (sum of heights + gaps, no trailing gap)
  const subColHeightMap = new Map<string, number>(); // `${layer}:${subColIdx}` → height
  byLayer.forEach((aliases, col) => {
    const numSubCols = layerSubColCount.get(col)!;
    const heights: number[] = Array(numSubCols).fill(0);
    aliases.forEach((alias, i) => {
      const subColIdx = Math.min(Math.floor(i / MAX_PER_COL), numSubCols - 1);
      heights[subColIdx] += computeErdHeight(classes.get(alias)!) + ROW_GAP;
    });
    heights.forEach((h, idx) => {
      subColHeightMap.set(`${col}:${idx}`, h > 0 ? h - ROW_GAP : 0);
    });
  });

  const maxColHeight = Array.from(subColHeightMap.values())
    .reduce((mx, h) => Math.max(mx, h), 0);

  // Second pass: assign positions; start each sub-column so its centre aligns
  // with the centre of the tallest sub-column (globally).
  const layouts = new Map<string, ClassLayout>();
  byLayer.forEach((aliases, col) => {
    const startX = layerStartX.get(col)!;
    const numSubCols = layerSubColCount.get(col)!;

    const subColY: number[] = Array.from({ length: numSubCols }, (_, idx) => {
      const h = subColHeightMap.get(`${col}:${idx}`) ?? 0;
      return ORIGIN_Y + Math.round((maxColHeight - h) / 2);
    });

    aliases.forEach((alias, i) => {
      const subColIdx = Math.min(Math.floor(i / MAX_PER_COL), numSubCols - 1);
      const erd = classes.get(alias)!;
      const h = computeErdHeight(erd);
      const x = startX + subColIdx * (CLASS_W + SUBCOL_GAP);
      const y = subColY[subColIdx];
      layouts.set(alias, { x, y, w: CLASS_W, h });
      subColY[subColIdx] += h + ROW_GAP;
    });
  });

  return layouts;
};

// Return the canvas-Y of the centre of a named field row, or null if not found.
const getFieldCenterY = (erd: ErdClassMeta, fieldName: string, box: ClassLayout): number | null => {
  const headerH = erd.stereotype ? ERD_HEADER_H_STEREO : ERD_HEADER_H_BASE;
  const lower = fieldName.toLowerCase();
  const idx = erd.fields.findIndex((f) => f.name.toLowerCase() === lower);
  if (idx < 0) return null;
  const rawY = box.y + headerH + ERD_SECTION_PAD + idx * ERD_LINE_H + ERD_LINE_H / 2;
  return Math.max(box.y + 4, Math.min(box.y + box.h - 4, rawY));
};

// Match relation label → field names in source / target class.
// FK heuristic: label "user_id" → fromField="user_id" (in FROM class),
//               toField="id" (looks for "id" field in TO class).
const detectFieldHints = (
  label: string | undefined,
  fromClass: ErdClassMeta | undefined,
  toClass: ErdClassMeta | undefined
): { fromField?: string; toField?: string } => {
  if (!label || !fromClass || !toClass) return {};
  const lab = label.toLowerCase().trim();

  const fromMatch = fromClass.fields.find((f) => f.name.toLowerCase() === lab);
  let toMatch = toClass.fields.find((f) => f.name.toLowerCase() === lab);
  if (!toMatch && lab.endsWith('_id')) {
    toMatch = toClass.fields.find((f) => f.name.toLowerCase() === 'id');
  }
  return { fromField: fromMatch?.name, toField: toMatch?.name };
};

const getPortPoint = (
  box: ClassLayout,
  side: RoutedRelation['startSide'],
  index: number,
  total: number,
  fieldY?: number          // if set, anchor at this Y (field-level port)
) => {
  if ((side === 'left' || side === 'right') && fieldY !== undefined) {
    return {
      x: side === 'left' ? box.x : box.x + box.w,
      y: Math.max(box.y + 4, Math.min(box.y + box.h - 4, fieldY)),
    };
  }

  const usable =
    side === 'left' || side === 'right'
      ? Math.max(0, box.h - PORT_MARGIN * 2)
      : Math.max(0, box.w - PORT_MARGIN * 2);
  const offset = total <= 1 ? usable / 2 : (usable / (total - 1)) * index;

  if (side === 'left' || side === 'right') {
    return {
      x: side === 'left' ? box.x : box.x + box.w,
      y: box.y + PORT_MARGIN + offset,
    };
  }

  return {
    x: box.x + PORT_MARGIN + offset,
    y: side === 'top' ? box.y : box.y + box.h,
  };
};

const getGroupSortValue = (
  rel: RoutedRelation,
  alias: string,
  side: RoutedRelation['startSide'],
  layouts: Map<string, ClassLayout>
) => {
  const otherAlias = rel.from === alias ? rel.to : rel.from;
  const otherCenter = getClassCenter(layouts.get(otherAlias)!);
  return side === 'left' || side === 'right' ? otherCenter.y : otherCenter.x;
};

const buildPortAssignments = (relations: RoutedRelation[], layouts: Map<string, ClassLayout>) => {
  // Field-pinned arrows skip the spread group; they always get index=0, total=1.
  const groups = new Map<string, RoutedRelation[]>();

  relations.forEach((rel) => {
    if (rel.startFieldY === undefined) {
      const startKey = `${rel.from}:${rel.startSide}`;
      groups.set(startKey, [...(groups.get(startKey) || []), rel]);
    }
    if (rel.endFieldY === undefined) {
      const endKey = `${rel.to}:${rel.endSide}`;
      groups.set(endKey, [...(groups.get(endKey) || []), rel]);
    }
  });

  const assignments = new Map<string, { index: number; total: number }>();
  groups.forEach((rels, groupKey) => {
    const [alias, side] = groupKey.split(':') as [string, RoutedRelation['startSide']];
    const ordered = [...rels].sort((a, b) => {
      const delta = getGroupSortValue(a, alias, side, layouts) - getGroupSortValue(b, alias, side, layouts);
      return delta !== 0 ? delta : a.relIndex - b.relIndex;
    });

    ordered.forEach((rel, idx) => {
      assignments.set(`${groupKey}:${rel.routeId}`, { index: idx, total: ordered.length });
    });
  });

  // Ensure every relation has an entry (field-pinned ones get dummy 0/1)
  relations.forEach((rel) => {
    const sk = `${rel.from}:${rel.startSide}:${rel.routeId}`;
    const ek = `${rel.to}:${rel.endSide}:${rel.routeId}`;
    if (!assignments.has(sk)) assignments.set(sk, { index: 0, total: 1 });
    if (!assignments.has(ek)) assignments.set(ek, { index: 0, total: 1 });
  });

  return assignments;
};

// ── Obstacle detection helpers ────────────────────────────────────────────

const segmentsIntersect = (
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean => {
  // Standard parametric line-line intersection (see Wikipedia: Line–line intersection).
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-9) return false;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
};

const lineCrossesRect = (
  x1: number, y1: number, x2: number, y2: number,
  rx: number, ry: number, rw: number, rh: number,
  margin: number = 0
): boolean => {
  const rxL = rx - margin;
  const rxR = rx + rw + margin;
  const ryT = ry - margin;
  const ryB = ry + rh + margin;
  if ((x1 > rxL && x1 < rxR && y1 > ryT && y1 < ryB) ||
      (x2 > rxL && x2 < rxR && y2 > ryT && y2 < ryB)) return true;
  return (
    segmentsIntersect(x1, y1, x2, y2, rxL, ryT, rxR, ryT) ||
    segmentsIntersect(x1, y1, x2, y2, rxR, ryT, rxR, ryB) ||
    segmentsIntersect(x1, y1, x2, y2, rxL, ryB, rxR, ryB) ||
    segmentsIntersect(x1, y1, x2, y2, rxL, ryT, rxL, ryB)
  );
};

const pathCrossesAnyTable = (
  x1: number, y1: number, x2: number, y2: number,
  from: string, to: string,
  layouts: Map<string, ClassLayout>,
  margin: number = 2
): boolean => {
  for (const [alias, box] of layouts) {
    if (alias === from || alias === to) continue;
    if (lineCrossesRect(x1, y1, x2, y2, box.x, box.y, box.w, box.h, margin)) return true;
  }
  return false;
};

/**
 * Picks a `laneX` for a 4-point elbow that keeps the vertical AND both
 * horizontal segments clear of every intermediate table.  Returns the chosen
 * laneX plus a `satisfiable` flag — when false, the caller should fall back
 * to a 6-point detour because no single laneX can satisfy all constraints.
 */
const findClearLaneX = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  from: string, to: string,
  layouts: Map<string, ClassLayout>,
  goRight: boolean,
  preferred: number,
  margin: number = 8,
  portInfo?: { index: number; total: number }
): { laneX: number; satisfiable: boolean } => {
  let lo = goRight ? start.x + margin : end.x + margin;
  let hi = goRight ? end.x - margin : start.x - margin;
  if (lo >= hi) return { laneX: preferred, satisfiable: false };

  const yLo = Math.min(start.y, end.y);
  const yHi = Math.max(start.y, end.y);
  const xLo = Math.min(start.x, end.x);
  const xHi = Math.max(start.x, end.x);

  for (const [alias, box] of layouts) {
    if (alias === from || alias === to) continue;
    const bL = box.x, bR = box.x + box.w;
    const bT = box.y, bB = box.y + box.h;
    if (bR <= xLo || bL >= xHi) continue;

    // H1 at y=start.y — first horizontal segment runs source→laneX.
    if (start.y > bT - margin && start.y < bB + margin) {
      if (goRight) hi = Math.min(hi, bL - margin);
      else lo = Math.max(lo, bR + margin);
    }
    // H2 at y=end.y — second horizontal segment runs laneX→target.
    if (end.y > bT - margin && end.y < bB + margin) {
      if (goRight) lo = Math.max(lo, bR + margin);
      else hi = Math.min(hi, bL - margin);
    }
    // V at x=laneX — vertical spine spans [yLo, yHi].
    if (yLo < bB && yHi > bT) {
      if (goRight) lo = Math.max(lo, bR + margin);
      else hi = Math.min(hi, bL - margin);
    }
  }

  if (lo > hi) return { laneX: preferred, satisfiable: false };

  // Multiple arrows sharing the same source port get evenly distributed across
  // the clear corridor — keeps parallel arrows from collapsing onto the same
  // laneX (which would also cause their labels to overlap).
  if (portInfo && portInfo.total > 1) {
    const t = (portInfo.index + 0.5) / portInfo.total;
    return { laneX: lo + t * (hi - lo), satisfiable: true };
  }
  return { laneX: Math.max(lo, Math.min(hi, preferred)), satisfiable: true };
};

/**
 * Picks a Y for the central horizontal segment of a 6-point detour path.
 * The Y must be clear of every table whose X range overlaps the source-to-
 * target X span.  Prefers the row gap closest to the source's centre line.
 */
const findClearDetourY = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  from: string, to: string,
  layouts: Map<string, ClassLayout>,
  margin: number = 8
): number => {
  const xLo = Math.min(start.x, end.x);
  const xHi = Math.max(start.x, end.x);
  const ranges: Array<[number, number]> = [];
  for (const [alias, box] of layouts) {
    if (alias === from || alias === to) continue;
    if (box.x + box.w <= xLo + margin || box.x >= xHi - margin) continue;
    ranges.push([box.y, box.y + box.h]);
  }
  if (ranges.length === 0) return (start.y + end.y) / 2;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && last[1] + margin * 2 >= r[0]) {
      last[1] = Math.max(last[1], r[1]);
    } else {
      merged.push([r[0], r[1]]);
    }
  }

  const candidates: number[] = [];
  candidates.push(merged[0][0] - margin * 2);
  candidates.push(merged[merged.length - 1][1] + margin * 2);
  for (let i = 0; i < merged.length - 1; i++) {
    candidates.push((merged[i][1] + merged[i + 1][0]) / 2);
  }

  const ideal = (start.y + end.y) / 2;
  candidates.sort((a, b) => Math.abs(a - ideal) - Math.abs(b - ideal));
  return candidates[0];
};

/**
 * Vertical-dominant counterpart of findClearLaneX.  The V-H-V elbow has two
 * vertical segments (at start.x and end.x) and a horizontal spine at laneY.
 * Returns a laneY that keeps all three clear, plus satisfiable flag.
 */
const findClearLaneY = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  from: string, to: string,
  layouts: Map<string, ClassLayout>,
  goDown: boolean,
  preferred: number,
  margin: number = 8,
  portInfo?: { index: number; total: number }
): { laneY: number; satisfiable: boolean } => {
  let lo = goDown ? start.y + margin : end.y + margin;
  let hi = goDown ? end.y - margin : start.y - margin;
  if (lo >= hi) return { laneY: preferred, satisfiable: false };

  const yLo = Math.min(start.y, end.y);
  const yHi = Math.max(start.y, end.y);
  const xLo = Math.min(start.x, end.x);
  const xHi = Math.max(start.x, end.x);

  for (const [alias, box] of layouts) {
    if (alias === from || alias === to) continue;
    const bL = box.x, bR = box.x + box.w;
    const bT = box.y, bB = box.y + box.h;
    if (bB <= yLo || bT >= yHi) continue;

    // V1 at x=start.x — first vertical runs source→(start.x, laneY).
    if (start.x > bL - margin && start.x < bR + margin) {
      if (goDown) hi = Math.min(hi, bT - margin);
      else lo = Math.max(lo, bB + margin);
    }
    // V2 at x=end.x — second vertical runs (end.x, laneY)→target.
    if (end.x > bL - margin && end.x < bR + margin) {
      if (goDown) lo = Math.max(lo, bB + margin);
      else hi = Math.min(hi, bT - margin);
    }
    // H at y=laneY — must clear any table whose X range overlaps the span.
    if (bL < xHi && bR > xLo) {
      if (goDown) lo = Math.max(lo, bB + margin);
      else hi = Math.min(hi, bT - margin);
    }
  }

  if (lo > hi) return { laneY: preferred, satisfiable: false };
  if (portInfo && portInfo.total > 1) {
    const t = (portInfo.index + 0.5) / portInfo.total;
    return { laneY: lo + t * (hi - lo), satisfiable: true };
  }
  return { laneY: Math.max(lo, Math.min(hi, preferred)), satisfiable: true };
};

/**
 * Picks an X for the central vertical segment of a vertical-dominant 6-point
 * detour path.  Mirror of findClearDetourY but on the X axis.
 */
const findClearDetourX = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  from: string, to: string,
  layouts: Map<string, ClassLayout>,
  margin: number = 8
): number => {
  const yLo = Math.min(start.y, end.y);
  const yHi = Math.max(start.y, end.y);
  const ranges: Array<[number, number]> = [];
  for (const [alias, box] of layouts) {
    if (alias === from || alias === to) continue;
    if (box.y + box.h <= yLo + margin || box.y >= yHi - margin) continue;
    ranges.push([box.x, box.x + box.w]);
  }
  if (ranges.length === 0) return (start.x + end.x) / 2;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && last[1] + margin * 2 >= r[0]) {
      last[1] = Math.max(last[1], r[1]);
    } else {
      merged.push([r[0], r[1]]);
    }
  }

  const candidates: number[] = [];
  candidates.push(merged[0][0] - margin * 2);
  candidates.push(merged[merged.length - 1][1] + margin * 2);
  for (let i = 0; i < merged.length - 1; i++) {
    candidates.push((merged[i][1] + merged[i + 1][0]) / 2);
  }

  const ideal = (start.x + end.x) / 2;
  candidates.sort((a, b) => Math.abs(a - ideal) - Math.abs(b - ideal));
  return candidates[0];
};

const buildPathPoints = (
  relation: RoutedRelation,
  start: { x: number; y: number },
  end: { x: number; y: number },
  startMeta: { index: number; total: number },
  endMeta: { index: number; total: number },
  /** Full layout map; enables obstacle-aware routing when provided. */
  layouts?: Map<string, ClassLayout>
) => {
  const hasFieldPin = relation.startFieldY !== undefined || relation.endFieldY !== undefined;
  const forceBent = hasFieldPin && Math.abs(start.y - end.y) > 2;

  // chooseRouteShape may have flagged this as "straight" based on angle alone.
  // Verify the straight 2-point path is actually clear of intermediate tables;
  // if not, fall through to bent routing so the arrow detours around them.
  if (!relation.bent && !forceBent) {
    if (!layouts || !pathCrossesAnyTable(start.x, start.y, end.x, end.y, relation.from, relation.to, layouts)) {
      return [start, end];
    }
  }

  if (relation.dominantAxis === 'horizontal') {
    const goRight = relation.startSide === 'right';
    const MARGIN = 8;
    const portSpread = hasFieldPin ? 0 : startMeta.index * ROUTE_SPREAD;
    const preferred = goRight
      ? start.x + MARGIN + portSpread
      : start.x - MARGIN - portSpread;

    if (!layouts) {
      const laneX = (start.x + end.x) / 2 +
        (hasFieldPin ? 0 : (startMeta.index - (startMeta.total - 1) / 2) * ROUTE_SPREAD);
      return [
        start,
        { x: laneX, y: start.y },
        { x: laneX, y: end.y },
        end,
      ];
    }

    const { laneX, satisfiable } = findClearLaneX(
      start, end, relation.from, relation.to, layouts, goRight, preferred,
      8, { index: startMeta.index, total: startMeta.total }
    );

    if (satisfiable) {
      return [
        start,
        { x: laneX, y: start.y },
        { x: laneX, y: end.y },
        end,
      ];
    }

    // 6-point detour: H1/H2 + V constraints can't all be satisfied by one
    // laneX (e.g. an aligned table sits directly in the path).  Step out from
    // both ports and traverse via a clear row gap that the straight line
    // would otherwise cut through.
    const detourY = findClearDetourY(start, end, relation.from, relation.to, layouts);
    const elbow1X = goRight ? start.x + MARGIN : start.x - MARGIN;
    const elbow2X = goRight ? end.x - MARGIN : end.x + MARGIN;
    return [
      start,
      { x: elbow1X, y: start.y },
      { x: elbow1X, y: detourY },
      { x: elbow2X, y: detourY },
      { x: elbow2X, y: end.y },
      end,
    ];
  }

  // Vertical dominant: mirrors the horizontal-dominant logic on the Y axis.
  const goDown = relation.startSide === 'bottom';
  const MARGIN_V = 8;
  const portSpreadV = hasFieldPin ? 0 : startMeta.index * ROUTE_SPREAD;
  const preferredV = goDown
    ? start.y + MARGIN_V + portSpreadV
    : start.y - MARGIN_V - portSpreadV;

  if (!layouts) {
    const laneBias = hasFieldPin
      ? 0
      : (startMeta.index - (startMeta.total - 1) / 2) * ROUTE_SPREAD;
    const laneY = (start.y + end.y) / 2 + laneBias;
    return [
      start,
      { x: start.x, y: laneY },
      { x: end.x, y: laneY },
      end,
    ];
  }

  const { laneY, satisfiable: vSatisfiable } = findClearLaneY(
    start, end, relation.from, relation.to, layouts, goDown, preferredV,
    8, { index: startMeta.index, total: startMeta.total }
  );

  if (vSatisfiable) {
    return [
      start,
      { x: start.x, y: laneY },
      { x: end.x, y: laneY },
      end,
    ];
  }

  // 6-point vertical detour: step out from both ports, traverse via a clear
  // column gap that the V-H-V elbow would otherwise cut through.
  const detourX = findClearDetourX(start, end, relation.from, relation.to, layouts);
  const elbow1Y = goDown ? start.y + MARGIN_V : start.y - MARGIN_V;
  const elbow2Y = goDown ? end.y - MARGIN_V : end.y + MARGIN_V;
  return [
    start,
    { x: start.x, y: elbow1Y },
    { x: detourX, y: elbow1Y },
    { x: detourX, y: elbow2Y },
    { x: end.x, y: elbow2Y },
    end,
  ];
};

// Builds a rectangular loop on the right side of a table for self-referential FKs.
// Multiple loops on the same table are staggered outward via loopIndex.
const buildSelfRefPath = (
  box: ClassLayout,
  loopIndex: number = 0
): {
  startPoint: { x: number; y: number };
  endPoint:   { x: number; y: number };
  pathPoints: Array<{ x: number; y: number }>;
  labelAnchor: { x: number; y: number };
} => {
  const loopX    = box.x + box.w + SELF_REF_LOOP_W + loopIndex * Math.round(SELF_REF_LOOP_W / 2);
  const startY   = box.y + box.h * 0.3;
  const endY     = box.y + box.h * 0.7;
  const startPoint = { x: box.x + box.w, y: startY };
  const endPoint   = { x: box.x + box.w, y: endY };
  return {
    startPoint,
    endPoint,
    pathPoints: [startPoint, { x: loopX, y: startY }, { x: loopX, y: endY }, endPoint],
    labelAnchor: { x: loopX + 4, y: (startY + endY) / 2 },
  };
};

const getLabelAnchor = (pathPoints: Array<{ x: number; y: number }>) => {
  if (!pathPoints || pathPoints.length === 0) return { x: 0, y: 0 };
  if (pathPoints.length === 1) return pathPoints[0];
  if (pathPoints.length === 2) {
    return {
      x: (pathPoints[0].x + pathPoints[1].x) / 2,
      y: (pathPoints[0].y + pathPoints[1].y) / 2,
    };
  }

  // For multi-segment paths (4-pt elbow or 6-pt detour) put the label on the
  // longest segment — that's the visually dominant span and it has the most
  // room.  Avoids stuffing labels onto the tiny start/end stubs of detours.
  let bestIdx = 0;
  let bestLen = -1;
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const dx = pathPoints[i + 1].x - pathPoints[i].x;
    const dy = pathPoints[i + 1].y - pathPoints[i].y;
    const len = Math.hypot(dx, dy);
    if (len > bestLen) {
      bestLen = len;
      bestIdx = i;
    }
  }
  return {
    x: (pathPoints[bestIdx].x + pathPoints[bestIdx + 1].x) / 2,
    y: (pathPoints[bestIdx].y + pathPoints[bestIdx + 1].y) / 2,
  };
};

const toClassLayout = (el: any): ClassLayout | null => {
  if (!el || typeof el.x !== 'number' || typeof el.y !== 'number') return null;
  if (typeof el.width === 'number' && typeof el.height === 'number') {
    return { x: el.x, y: el.y, w: el.width, h: el.height };
  }
  return null;
};

export const isErdArrowElement = (el: any): boolean =>
  !!(
    el &&
    el.type === 'arrow' &&
    (el.erdRoute ||
      (typeof el.startCap === 'string' && el.startCap.startsWith('erd-')) ||
      (typeof el.endCap === 'string' && el.endCap.startsWith('erd-')))
  );

export function rerouteErdElements(elements: any[]): any[] {
  const layouts = new Map<string, ClassLayout>();
  // Also collect ErdClassMeta so we can recompute field-level Y after drag
  const erdMeta = new Map<string, ErdClassMeta>();
  elements.forEach((el) => {
    const layout = toClassLayout(el);
    if (layout) layouts.set(el.id, layout);
    if (el.erd) erdMeta.set(el.id, el.erd as ErdClassMeta);
  });

  const routedRelations: RoutedRelation[] = elements
    .filter((el) => isErdArrowElement(el) && el.startConnection?.elementId && el.endConnection?.elementId
                    && el.startConnection.elementId !== el.endConnection.elementId)  // self-refs handled separately
    .map((arrow, relIndex) => {
      const from = arrow.startConnection.elementId;
      const to = arrow.endConnection.elementId;
      const fromBox = layouts.get(from);
      const toBox = layouts.get(to);
      if (!fromBox || !toBox) return null;

      const shape = chooseRouteShape(fromBox, toBox);

      // Restore field hints stored in erdRoute and recompute Y from current positions
      const fromField: string | undefined = arrow.erdRoute?.fromField ?? undefined;
      const toField: string | undefined = arrow.erdRoute?.toField ?? undefined;
      const fromErd = erdMeta.get(from);
      const toErd = erdMeta.get(to);

      const startFieldY =
        fromField && fromErd && (shape.startSide === 'left' || shape.startSide === 'right')
          ? getFieldCenterY(fromErd, fromField, fromBox) ?? undefined
          : undefined;
      const endFieldY =
        toField && toErd && (shape.endSide === 'left' || shape.endSide === 'right')
          ? getFieldCenterY(toErd, toField, toBox) ?? undefined
          : undefined;

      return {
        from,
        to,
        startCap: arrow.startCap || 'none',
        endCap: arrow.endCap || 'none',
        dashed: !!arrow.dashed,
        routeId: arrow.id,
        relIndex,
        fromField,
        toField,
        startFieldY,
        endFieldY,
        ...shape,
      };
    })
    .filter(Boolean) as RoutedRelation[];

  // Even with no normal arrows we still need to update self-ref loops on drag,
  // so we remove the early-exit and guard port-assignment instead.
  const portAssignments = routedRelations.length
    ? buildPortAssignments(routedRelations, layouts)
    : new Map<string, { index: number; total: number }>();
  const relationById = new Map(routedRelations.map((rel) => [rel.routeId, rel]));

  const rerouted = elements.map((el) => {
    if (!isErdArrowElement(el)) return el;

    // Self-referential arrow: recompute rectangular loop from the table's current position.
    if (el.startConnection?.elementId && el.startConnection.elementId === el.endConnection?.elementId) {
      const box = layouts.get(el.startConnection.elementId);
      if (!box) return el;
      const { startPoint, endPoint, pathPoints } = buildSelfRefPath(box, el.erdRoute?.loopIndex ?? 0);
      return {
        ...el,
        x: startPoint.x, y: startPoint.y,
        x2: endPoint.x,  y2: endPoint.y,
        pathPoints,
        startConnection: { elementId: el.startConnection.elementId, side: 'right' },
        endConnection:   { elementId: el.endConnection.elementId,   side: 'right' },
      };
    }

    const rel = relationById.get(el.id);
    if (!rel) return el;

    const fromBox = layouts.get(rel.from)!;
    const toBox = layouts.get(rel.to)!;
    const startMeta = portAssignments.get(`${rel.from}:${rel.startSide}:${rel.routeId}`)!;
    const endMeta = portAssignments.get(`${rel.to}:${rel.endSide}:${rel.routeId}`)!;
    const startPoint = getPortPoint(fromBox, rel.startSide, startMeta.index, startMeta.total, rel.startFieldY);
    const endPoint = getPortPoint(toBox, rel.endSide, endMeta.index, endMeta.total, rel.endFieldY);
    const pathPoints = buildPathPoints(rel, startPoint, endPoint, startMeta, endMeta, layouts);

    return {
      ...el,
      x: startPoint.x,
      y: startPoint.y,
      x2: endPoint.x,
      y2: endPoint.y,
      pathPoints,
      startConnection: { elementId: rel.from, side: rel.startSide },
      endConnection: { elementId: rel.to, side: rel.endSide },
      erdRoute: {
        ...(el.erdRoute || {}),
        dominantAxis: rel.dominantAxis,
        bent: rel.bent,
        startSide: rel.startSide,
        endSide: rel.endSide,
        startPortIndex: startMeta.index,
        startPortCount: startMeta.total,
        endPortIndex: endMeta.index,
        endPortCount: endMeta.total,
        fromField: rel.fromField ?? null,
        toField: rel.toField ?? null,
      },
    };
  });

  const arrowMap = new Map<string, any>();
  rerouted.forEach((el) => {
    if (el.type === 'arrow') arrowMap.set(el.id, el);
  });

  return rerouted.map((el) => {
    if (el.type !== 'text' || !el.attachedToArrow) return el;
    const arrow = arrowMap.get(el.attachedToArrow);
    if (!arrow) return el;

    const anchor = getLabelAnchor(
      arrow.pathPoints?.length
        ? arrow.pathPoints
        : [{ x: arrow.x, y: arrow.y }, { x: arrow.x2, y: arrow.y2 }]
    );
    return { ...el, x: anchor.x, y: anchor.y };
  });
}

export function parseErdDsl(text: string, ElementFactory: any): any[] {
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim());

  const classes = new Map<string, ErdClassMeta>();
  const classOrder: string[] = [];
  const relations: Relation[] = [];
  const notes: Array<{ text: string; id: string }> = [];

  let inBlock = false;
  let currentClass: ErdClassMeta | null = null;
  let currentClassAlias = '';
  let noteIdx = 0;
  const relCountsSeen: Record<string, number> = {};

  const addClass = (alias: string, meta: ErdClassMeta) => {
    if (!classes.has(alias)) {
      classes.set(alias, meta);
      classOrder.push(alias);
    }
  };

  for (const line of lines) {
    if (/^@startuml/i.test(line)) { inBlock = true; continue; }
    if (/^@enduml/i.test(line)) { inBlock = false; continue; }
    if (!inBlock) continue;
    if (/^'/.test(line) || /^\/\//.test(line)) continue;

    // Closing brace
    if (line === '}') {
      if (currentClass) {
        addClass(currentClassAlias, currentClass);
        currentClass = null;
        currentClassAlias = '';
      }
      continue;
    }

    // Inside class body
    if (currentClass) {
      if (!line) continue;
      const isMethod = line.includes('(');
      const field = parseFieldLine(line);
      if (isMethod) currentClass.methods.push(field);
      else currentClass.fields.push(field);
      continue;
    }

    // Note
    let nm = NOTE_RE.exec(line);
    if (nm) { notes.push({ text: nm[1], id: nm[2] }); continue; }
    nm = NOTE_SINGLE_RE.exec(line);
    if (nm) { notes.push({ text: nm[1], id: `note_${noteIdx++}` }); continue; }

    // Package (just skip; members will still be parsed)
    if (PACKAGE_RE.test(line)) continue;

    // Class / Interface / Abstract
    const cm = CLASS_HEADER_RE.exec(line);
    if (cm) {
      const keyword = cm[1].toLowerCase().replace(/\s+/, ' ');
      const name = cm[2];
      const stereotype = cm[3];
      const alias = cm[4] || name;
      const kind: ErdClassMeta['kind'] = keyword.includes('interface')
        ? 'interface'
        : keyword.includes('abstract')
        ? 'abstract'
        : 'class';
      const meta: ErdClassMeta = { kind, name, stereotype, fields: [], methods: [] };
      if (!line.endsWith('{')) {
        addClass(alias, meta);
      } else {
        currentClass = meta;
        currentClassAlias = alias;
      }
      continue;
    }

    // Enum
    const em = ENUM_HEADER_RE.exec(line);
    if (em) {
      const name = em[1];
      const stereotype = em[2];
      const meta: ErdClassMeta = { kind: 'enum', name, stereotype, fields: [], methods: [] };
      if (!line.endsWith('{')) {
        addClass(name, meta);
      } else {
        currentClass = meta;
        currentClassAlias = name;
      }
      continue;
    }

    // Stereotype declaration: "ClassName" <<Entity>>
    const sd = STEREOTYPE_DECL_RE.exec(line);
    if (sd) {
      const existing = classes.get(sd[1]);
      if (existing) existing.stereotype = sd[2];
      continue;
    }

    // Relations
    for (const pat of REL_PATTERNS) {
      const rm = pat.re.exec(line);
      if (rm) {
        relations.push({
          from: rm[1], to: rm[2],
          startCap: pat.startCap, endCap: pat.endCap,
          dashed: pat.dashed,
          label: rm[3]?.trim(),
        });
        [rm[1], rm[2]].forEach((name) => {
          if (!classes.has(name)) {
            addClass(name, { kind: 'class', name, fields: [], methods: [] });
          }
        });
        break;
      }
    }
  }

  // ── Build elements ──────────────────────────────────────────────────────

  const elements: any[] = [];
  const gap = relations.reduce((max, rel) => Math.max(max, relationLabelGap(rel.label)), BASE_GAP);
  const classPositions = buildClassLayouts(classOrder, classes, relations, gap);
  const classIds = new Map<string, string>();

  classOrder.forEach((alias) => {
    const erd = classes.get(alias)!;
    const { x, y, w, h } = classPositions.get(alias)!;

    const el = createElement('erd-class', x, y, {
      width: w,
      height: h,
      fill: '#FFFFFF',
      stroke: '#94A3B8',
      strokeWidth: 1,
      erd,
      _semanticId: `erd-class:${erd.name}`,
    });
    classIds.set(alias, el.id);
    elements.push(el);
  });

  // Split self-referential relations (from === to) from normal ones.
  // Self-refs render as rectangular loops and must not participate in port
  // assignment alongside regular arrows (they'd corrupt spread indices).
  const selfRefRels: Relation[] = [];
  const normalRels: Relation[]  = [];
  for (const rel of relations) {
    if (rel.from === rel.to) selfRefRels.push(rel);
    else normalRels.push(rel);
  }

  const routedRelations: RoutedRelation[] = normalRels.map((rel, idx) => {
    const relKey = `${rel.from}:${rel.to}:${rel.endCap || rel.startCap}`;
    relCountsSeen[relKey] = (relCountsSeen[relKey] || 0) + 1;
    const routeId = `${rel.from}:${rel.to}:${rel.endCap || rel.startCap}:${relCountsSeen[relKey]}`;
    const fromPos = classPositions.get(rel.from)!;
    const toPos = classPositions.get(rel.to)!;
    const shape = chooseRouteShape(fromPos, toPos);

    // Detect which fields the relation originates/terminates at
    const hints = detectFieldHints(rel.label, classes.get(rel.from), classes.get(rel.to));

    // Compute canvas Y overrides for field-level ports (left/right sides only)
    const startFieldY =
      hints.fromField && (shape.startSide === 'left' || shape.startSide === 'right')
        ? getFieldCenterY(classes.get(rel.from)!, hints.fromField, fromPos) ?? undefined
        : undefined;
    const endFieldY =
      hints.toField && (shape.endSide === 'left' || shape.endSide === 'right')
        ? getFieldCenterY(classes.get(rel.to)!, hints.toField, toPos) ?? undefined
        : undefined;

    return {
      ...rel,
      ...hints,
      relIndex: idx,
      routeId,
      ...shape,
      startFieldY,
      endFieldY,
    };
  });

  const portAssignments = buildPortAssignments(routedRelations, classPositions);

  routedRelations.forEach((rel) => {
    const fromPos = classPositions.get(rel.from)!;
    const toPos = classPositions.get(rel.to)!;
    const startMeta = portAssignments.get(`${rel.from}:${rel.startSide}:${rel.routeId}`)!;
    const endMeta = portAssignments.get(`${rel.to}:${rel.endSide}:${rel.routeId}`)!;
    const startPoint = getPortPoint(fromPos, rel.startSide, startMeta.index, startMeta.total, rel.startFieldY);
    const endPoint = getPortPoint(toPos, rel.endSide, endMeta.index, endMeta.total, rel.endFieldY);
    const pathPoints = buildPathPoints(rel, startPoint, endPoint, startMeta, endMeta, classPositions);

    const arrow = createElement('arrow', startPoint.x, startPoint.y, {
      x2: endPoint.x,
      y2: endPoint.y,
      startCap: rel.startCap === 'none' ? undefined : rel.startCap,
      endCap: rel.endCap === 'none' ? undefined : rel.endCap,
      dashed: rel.dashed,
      stroke: '#64748B',
      strokeWidth: 1.5,
      pathPoints,
      startConnection: { elementId: classIds.get(rel.from), side: rel.startSide },
      endConnection: { elementId: classIds.get(rel.to), side: rel.endSide },
      erd: { relType: rel.endCap !== 'none' ? rel.endCap : rel.startCap },
      erdRoute: {
        dominantAxis: rel.dominantAxis,
        bent: rel.bent,
        startSide: rel.startSide,
        endSide: rel.endSide,
        startPortIndex: startMeta.index,
        startPortCount: startMeta.total,
        endPortIndex: endMeta.index,
        endPortCount: endMeta.total,
        fromField: rel.fromField ?? null,
        toField: rel.toField ?? null,
      },
      _semanticId: `erd-rel:${rel.routeId}`,
    });
    elements.push(arrow);

    if (rel.label) {
      const anchor = getLabelAnchor(pathPoints);
      const labelEl = createElement('text', anchor.x, anchor.y, {
        content: rel.label,
        fontSize: 11,
        fill: '#64748B',
        _semanticId: `erd-rel-label:${rel.from}:${rel.to}:${rel.label}`,
        attachedToArrow: arrow.id,
      });
      elements.push(labelEl);
    }
  });

  // ── Self-referential arrows: rectangular loop on the right side ──────────
  const selfRefLoopIdx = new Map<string, number>();
  selfRefRels.forEach((rel) => {
    const box = classPositions.get(rel.from);
    if (!box) return;

    const loopIndex = selfRefLoopIdx.get(rel.from) ?? 0;
    selfRefLoopIdx.set(rel.from, loopIndex + 1);

    const relKey = `${rel.from}:${rel.to}:${rel.endCap || rel.startCap}`;
    relCountsSeen[relKey] = (relCountsSeen[relKey] || 0) + 1;
    const routeId = `${rel.from}:${rel.to}:${rel.endCap || rel.startCap}:${relCountsSeen[relKey]}`;

    const { startPoint, endPoint, pathPoints, labelAnchor } = buildSelfRefPath(box, loopIndex);

    const arrow = createElement('arrow', startPoint.x, startPoint.y, {
      x2: endPoint.x,
      y2: endPoint.y,
      startCap: rel.startCap === 'none' ? undefined : rel.startCap,
      endCap: rel.endCap === 'none' ? undefined : rel.endCap,
      dashed: rel.dashed,
      stroke: '#64748B',
      strokeWidth: 1.5,
      pathPoints,
      startConnection: { elementId: classIds.get(rel.from), side: 'right' },
      endConnection:   { elementId: classIds.get(rel.to),   side: 'right' },
      erd: { relType: rel.endCap !== 'none' ? rel.endCap : rel.startCap },
      erdRoute: {
        dominantAxis: 'horizontal',
        bent: true,
        startSide: 'right',
        endSide: 'right',
        startPortIndex: 0,
        startPortCount: 1,
        endPortIndex: 0,
        endPortCount: 1,
        fromField: null,
        toField: null,
        selfRef: true,
        loopIndex,
      },
      _semanticId: `erd-rel:${routeId}`,
    });
    elements.push(arrow);

    if (rel.label) {
      const labelEl = createElement('text', labelAnchor.x, labelAnchor.y, {
        content: rel.label,
        fontSize: 11,
        fill: '#64748B',
        _semanticId: `erd-rel-label:${rel.from}:${rel.to}:${rel.label}`,
        attachedToArrow: arrow.id,
      });
      elements.push(labelEl);
    }
  });

  const notesStartX = Array.from(classPositions.values())
    .reduce((mx, p) => Math.max(mx, p.x + p.w), ORIGIN_X) + gap;
  notes.forEach((note, i) => {
    const x = notesStartX + 20;
    const y = ORIGIN_Y + i * 80;
    const noteEl = createElement('text', x, y, {
      content: note.text,
      width: 160,
      height: 60,
      fill: '#FFFBEB',
      stroke: '#FCD34D',
      strokeWidth: 1,
      _semanticId: `erd-note:${note.id}`,
    });
    elements.push(noteEl);
  });

  return elements;
}

/** Returns true if text looks like a PlantUML class diagram (not sequence). */
export function isErdDsl(text: string): boolean {
  if (!text) return false;
  const t = text.trim();
  if (!t.startsWith('@startuml')) return false;
  const hasClass = /^\s*(class|interface|abstract\s+class|enum)\s/m.test(t);
  const hasRelation = /^\s*\S+\s+(--|>|\.\.|>|\*--|--\*|o--|--o|-->|\.\.>)\s+\S+/m.test(t);
  const hasSeqMsg = /^\s*\S+\s*(-+>|<-+)\s*\S+/m.test(t);
  return (hasClass || hasRelation) && !hasSeqMsg;
}
